import { useEffect, useMemo, useRef } from 'preact/hooks';
import { THREE, OrbitControls } from '../../../lib/three-client.js';
import {
  createElectricLine,
  createParcelMesh,
  createParcelOutline,
  createRoadFrontage,
  createSectionLines,
  createSideWalls,
  fitCameraToObject,
  projectWorldToScreen,
  toThreePos,
} from './createParcelMesh.js';

function supportsWebGL() {
  try {
    const c = document.createElement('canvas');
    return Boolean(c.getContext('webgl') || c.getContext('experimental-webgl'));
  } catch {
    return false;
  }
}

function meshDataFingerprint(data) {
  if (!data || typeof data !== 'object') return '';
  const mesh = data.mesh || {};
  const verts = Array.isArray(mesh.vertices) ? mesh.vertices.length : 0;
  const faces = Array.isArray(mesh.faces) ? mesh.faces.length : (Array.isArray(mesh.triangles) ? mesh.triangles.length : 0);
  const sid = data.snapshot_id ?? data.parcel?.ada ?? data.parcel?.parsel ?? '';
  const gen = data.generated_at || data.built_at || '';
  const mean = data.stats?.mean_slope ?? data.stats?.avg_slope ?? '';
  return `${sid}:${verts}:${faces}:${gen}:${mean}`;
}

export default function ParcelSlopeThreeScene({
  data,
  onLabelsChange,
  onUnsupported,
  onReady = null,
  compact = false,
}) {
  const hostRef = useRef(null);
  const stateRef = useRef(null);
  const onLabelsChangeRef = useRef(onLabelsChange);
  const onUnsupportedRef = useRef(onUnsupported);
  const onReadyRef = useRef(onReady);
  const dataRef = useRef(data);
  onLabelsChangeRef.current = onLabelsChange;
  onUnsupportedRef.current = onUnsupported;
  onReadyRef.current = onReady;
  dataRef.current = data;

  const fingerprint = useMemo(() => meshDataFingerprint(data), [data]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !fingerprint) return undefined;

    if (!supportsWebGL()) {
      onUnsupportedRef.current?.();
      return undefined;
    }

    const sceneData = dataRef.current;
    const width = Math.max(host.clientWidth, 280);
    const height = Math.max(host.clientHeight, 240);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf8fafc);

    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1000, 1000);
    camera.position.set(80, 90, -110);
    camera.up.set(0, 1, 0);

    const renderer = new THREE.WebGLRenderer({
      antialias: !compact,
      alpha: false,
      powerPreference: 'low-power',
      preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, compact ? 1.25 : 2));
    renderer.setSize(width, height, false);
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.display = 'block';
    host.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0xffffff, 0.72);
    scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 0.85);
    dir.position.set(80, 140, -60);
    scene.add(dir);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minZoom = 0.55;
    controls.maxZoom = 4.5;
    controls.maxPolarAngle = Math.PI * 0.48;
    controls.minPolarAngle = Math.PI * 0.12;

    const root = new THREE.Group();
    root.name = 'parcelRoot';
    scene.add(root);

    const parcelMesh = createParcelMesh(sceneData);
    if (parcelMesh) root.add(parcelMesh);
    const walls = createSideWalls(sceneData, parcelMesh);
    if (walls) root.add(walls);
    const outline = createParcelOutline(sceneData, parcelMesh);
    if (outline) root.add(outline);
    const road = createRoadFrontage(sceneData, parcelMesh);
    if (road) root.add(road);
    const sections = createSectionLines(sceneData, parcelMesh);
    if (sections) root.add(sections);
    const electric = createElectricLine(sceneData, parcelMesh);
    if (electric) root.add(electric);

    fitCameraToObject(camera, root, controls, { width, height });

    let raf = 0;
    let frame = 0;
    let visible = true;
    let readySent = false;

    const updateLabels = () => {
      const onLabels = onLabelsChangeRef.current;
      if (compact || !onLabels || !parcelMesh) return;
      const live = dataRef.current;
      const { minZ, heightScale, vertices } = parcelMesh.userData;
      const labels = [];

      const mid = new THREE.Vector3();
      const box = new THREE.Box3().setFromObject(root);
      box.getCenter(mid);
      const aspectScreen = projectWorldToScreen(renderer, camera, mid, host);
      labels.push({
        id: 'aspect',
        text: `en dik yon ${live?.stats?.direction_degree != null ? `${Math.round(live.stats.direction_degree)}°` : '—'}`,
        x: aspectScreen.x,
        y: aspectScreen.y - 18,
      });

      if (road?.userData?.mid) {
        const p = projectWorldToScreen(renderer, camera, road.userData.mid, host);
        const len = road.userData.length_m;
        labels.push({
          id: 'road',
          text: len != null ? `${Number(len).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} m` : 'yol',
          x: p.x,
          y: p.y - 10,
          tone: 'road',
        });
      }

      if (electric?.userData?.mid) {
        const p = projectWorldToScreen(renderer, camera, electric.userData.mid, host);
        const kw = electric.userData.kw;
        const area = electric.userData.area;
        const parts = [];
        if (kw) parts.push(String(kw).toLowerCase());
        if (area) parts.push(String(area).toLowerCase());
        labels.push({
          id: 'electric',
          text: parts.length ? parts.join(' · ') : 'yg hatti',
          x: p.x,
          y: p.y - 12,
          tone: 'electric',
        });
      }

      for (const key of ['a', 'b']) {
        const section = live?.sections?.[key];
        const pts = section?.points || [];
        if (pts.length < 2) continue;
        const start = toThreePos(pts[0], minZ, heightScale);
        const end = toThreePos(pts[pts.length - 1], minZ, heightScale);
        const s = projectWorldToScreen(renderer, camera, new THREE.Vector3(...start), host);
        const e = projectWorldToScreen(renderer, camera, new THREE.Vector3(...end), host);
        const tag = key.toUpperCase();
        labels.push({ id: `${key}-start`, text: tag, x: s.x, y: s.y - 8 });
        labels.push({ id: `${key}-end`, text: `${tag}'`, x: e.x, y: e.y - 8 });
      }

      const outlineIds = live?.mesh?.outline || [];
      outlineIds.slice(0, 4).forEach((id, idx) => {
        const v = vertices[id];
        if (!v) return;
        const [x, y, z] = toThreePos(v, minZ, heightScale);
        const p = projectWorldToScreen(renderer, camera, new THREE.Vector3(x, y + 0.2, z), host);
        labels.push({
          id: `elev-${idx}`,
          text: `${Number(v.z).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}m`,
          x: p.x + 4,
          y: p.y - 4,
          tone: 'elev',
        });
      });

      onLabels(labels);
    };

    const tick = () => {
      if (!visible) {
        raf = requestAnimationFrame(tick);
        return;
      }
      controls.update();
      renderer.render(scene, camera);
      frame += 1;
      if (!compact && frame % 3 === 0) updateLabels();
      if (!readySent && frame >= 2) {
        readySent = true;
        try { onReadyRef.current?.(); } catch {}
      }
      raf = requestAnimationFrame(tick);
    };
    tick();

    const onContextLost = (event) => {
      event.preventDefault();
    };
    const onContextRestored = () => {
      const w = Math.max(host.clientWidth, 200);
      const h = Math.max(host.clientHeight, 180);
      renderer.setSize(w, h, false);
      renderer.render(scene, camera);
    };
    renderer.domElement.addEventListener('webglcontextlost', onContextLost, false);
    renderer.domElement.addEventListener('webglcontextrestored', onContextRestored, false);

    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver((entries) => {
        const rect = entries[0]?.contentRect;
        if (!rect || rect.width < 2 || rect.height < 2) return;
        const w = Math.max(Math.round(rect.width), 200);
        const h = Math.max(Math.round(rect.height), 180);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(w, h, false);
        fitCameraToObject(camera, root, controls, { width: w, height: h });
        renderer.render(scene, camera);
      })
      : null;
    if (ro) ro.observe(host);

    let io = null;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver((entries) => {
        const hit = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0);
        visible = hit;
        if (!hit) return;
        const w = Math.max(host.clientWidth, 200);
        const h = Math.max(host.clientHeight, 180);
        if (w < 2 || h < 2) return;
        renderer.setSize(w, h, false);
        renderer.render(scene, camera);
      }, { root: null, threshold: [0, 0.01, 0.1] });
      io.observe(host);
    }

    const onVisibility = () => {
      if (document.hidden) return;
      renderer.render(scene, camera);
    };
    document.addEventListener('visibilitychange', onVisibility);

    stateRef.current = { scene, camera, renderer, controls, root };

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      io?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      renderer.domElement.removeEventListener('webglcontextlost', onContextLost);
      renderer.domElement.removeEventListener('webglcontextrestored', onContextRestored);
      controls.dispose();
      renderer.dispose();
      root.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
          else obj.material.dispose();
        }
      });
      if (renderer.domElement.parentNode === host) {
        host.removeChild(renderer.domElement);
      }
      stateRef.current = null;
    };
  }, [fingerprint, compact]);

  return <div ref={hostRef} className="pp-parcel-slope-three__canvas-host" />;
}
